import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import Button from '../ui/Button'
import { useCartStore } from '@/store/cartStore'
import { useAuth } from '@/contexts/AuthContext'
import { signOut } from '@/lib/auth'
import { Menu, X, User, LogOut, Settings, Heart, MapPin, Phone, ShoppingCart } from 'lucide-react'
import PotIcon from '../ui/PotIcon'

export default function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const totalItems = useCartStore(state => state.getTotalItems())
  const { profile, isAuthenticated } = useAuth()

  const isActive = (path: string) => location.pathname === path

  const handleSignOut = async () => {
    try {
      await signOut()
      setIsUserMenuOpen(false)
      navigate('/')
    } catch (error) {
      console.error('Çıkış hatası:', error)
    }
  }

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-2 text-xs sm:text-sm">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <div className="flex items-center gap-1">
                <Phone className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <a 
                  href="tel:+902126261131" 
                  className="whitespace-nowrap hover:underline transition-colors"
                  title="Bizi Arayın"
                >
                  Bizi Arayın
                </a>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="text-center sm:text-left">Teslimatta İstanbul - Avrupa Yakası</span>
              </div>
            </div>
            <div className="text-center sm:text-right">
              <span className="font-medium text-xs sm:text-sm">Taze börekler her gün hazırlanır</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-white shadow-lg border-b border-purple-100">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group flex-shrink-0">
              <div className="relative">
                <img 
                  src="/images/logo/logo_petunya_borek.png" 
                  alt="Petunya Börek" 
                  className="h-8 w-auto sm:h-12 transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg sm:text-xl font-bold text-purple-700">Petunya Börek</h1>
                <p className="text-xs sm:text-sm text-purple-600 font-medium">Lezzet & Kalite</p>
              </div>
            </Link>
          
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {[
                { path: '/', label: 'Ana Sayfa' },
                { path: '/urunler', label: 'Ürünler' },
                { path: '/hakkimizda', label: 'Hakkımızda' },
                { path: '/vizyon-misyon', label: 'Vizyon & Misyon' },
                { path: '/kalite-politikasi', label: 'Kalite Politikası' }
              ].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3 xl:px-4 py-2 rounded-lg font-medium transition-colors duration-200 text-sm xl:text-base ${
                    isActive(item.path)
                      ? 'text-primary-700 bg-primary-50'
                      : 'text-neutral-600 hover:text-primary-700 hover:bg-primary-50'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center space-x-2 sm:space-x-3">
              {/* Favorites Link - Only for authenticated users */}
              {isAuthenticated && (
                <Link to="/favorites">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="flex items-center gap-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                  >
                    <Heart className="w-4 h-4" />
                    <span className="hidden xl:inline">Favoriler</span>
                  </Button>
                </Link>
              )}

              {/* Cart Button */}
              <Link to="/sepet">
                <Button 
                  variant="primary" 
                  size="md" 
                  className="group relative flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <PotIcon size={18} showBadge={false} className="text-white flex-shrink-0" />
                  <span className="font-medium">Sepet ({totalItems})</span>
                  {totalItems > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-bounce">
                      {totalItems > 9 ? '9+' : totalItems}
                    </span>
                  )}
                </Button>
              </Link>

              {/* User Menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-600 flex-shrink-0" />
                    <span className="font-medium text-gray-700 hidden xl:inline text-sm">
                      {profile?.full_name?.split(' ')[0] || 'Kullanıcı'}
                    </span>
                  </button>

                  {/* User Dropdown */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        Profilim
                      </Link>
                      <Link
                        to="/orders"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Siparişlerim
                      </Link>
                      <Link
                        to="/siparis-takip"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Sipariş Takibi
                      </Link>
                      <hr className="my-2 border-gray-200" />
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        Çıkış Yap
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link to="/auth/login">
                    <Button variant="outline" size="sm" className="border-purple-600 text-purple-600 hover:bg-purple-50 text-xs sm:text-sm">
                      Giriş Yap
                    </Button>
                  </Link>
                  <Link to="/auth/register">
                    <Button variant="primary" size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm">
                      Kayıt Ol
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg bg-purple-50 hover:bg-purple-100 border border-purple-200 transition-colors flex-shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              ) : (
                <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-purple-100 bg-white">
              <nav className="flex flex-col space-y-2">
                {[
                  { path: '/', label: 'Ana Sayfa' },
                  { path: '/urunler', label: 'Ürünler' },
                  { path: '/hakkimizda', label: 'Hakkımızda' },
                  { path: '/vizyon-misyon', label: 'Vizyon & Misyon' },
                  { path: '/kalite-politikasi', label: 'Kalite Politikası' }
                ].map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-4 py-3 rounded-lg font-medium transition-colors text-sm ${
                      isActive(item.path)
                        ? 'text-primary-700 bg-primary-50'
                        : 'text-neutral-600 hover:text-primary-700 hover:bg-primary-50'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="truncate">{item.label}</span>
                  </Link>
                ))}
                
                <div className="pt-4 space-y-3">
                  {/* Favorites Link - Only for authenticated users */}
                  {isAuthenticated && (
                    <Link to="/favorites" onClick={() => setIsMobileMenuOpen(false)}>
                      <Button 
                        variant="outline" 
                        size="md" 
                        className="w-full flex items-center justify-center gap-2 border-purple-600 text-purple-600 hover:bg-purple-50 text-sm"
                      >
                        <Heart className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">Favoriler</span>
                      </Button>
                    </Link>
                  )}
              
                  <Link to="/sepet" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button 
                      variant="primary" 
                      size="md" 
                      className="group relative w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-sm"
                    >
                      <PotIcon size={18} showBadge={false} className="text-white flex-shrink-0" />
                      <span className="font-medium truncate">Sepet ({totalItems})</span>
                      {totalItems > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-bounce">
                          {totalItems > 9 ? '9+' : totalItems}
                        </span>
                      )}
                    </Button>
                  </Link>

                  {!isAuthenticated && (
                    <div className="flex gap-2">
                      <Link to="/auth/login" onClick={() => setIsMobileMenuOpen(false)} className="flex-1">
                        <Button variant="outline" size="md" className="w-full border-purple-600 text-purple-600 hover:bg-purple-50 text-sm">
                          Giriş Yap
                        </Button>
                      </Link>
                      <Link to="/auth/register" onClick={() => setIsMobileMenuOpen(false)} className="flex-1">
                        <Button variant="primary" size="md" className="w-full bg-purple-600 hover:bg-purple-700 text-sm">
                          Kayıt Ol
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  )
}